import { FC } from 'react'
import AppraisalFooter from '../../../../components/custom_components/Appraisal/AppraisalFooter'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import useOneOffPaymentTargetPaymentTargetCorrectionPaymentObjectivesAndTargetBonusOfYear from '../../../../graphql/one-off-payment/hooks/useOneOffPaymentTargetPaymentTargetCorrectionPaymentObjectivesAndTargetBonusOfYear'
import {
  Currency,
  FullMeetingFragment,
  MeetingStepStatus,
  MeetingStepViewId,
  MeetingType,
  ObjectivePaymentStatus,
  ObjectiveStatus,
} from '../../../../types'
import { PlannedPaymentContainer } from '../../../objectivePayment/PlannedPaymentContainer'
import { EditableOneOffPayment } from '../../one-off-payment/EditableOneOffPayment'
import { EditableTargetCorrectionPayment } from '../../target-correction-payment/EditableTargetCorrectionPayment'
import { AppraisalStepBtns } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { PaymentFooter } from '../shared/PaymentFooter'
import useCountrySettings from '../../../../graphql/countrySettings/hooks/useCountrySettings'
import { useTranslation } from 'react-i18next'
import { existingWrongTCPaymentsForLowEmployee, round2digits } from './util/paymentsUtil'
import { isDefined } from '../../../../types.helper'
import { retrieveCurrencyConversionRate } from '../../../../shared/currencyConversionUtil'
import { useCurrency } from '../../../../context/currency-context'
import { filterObjectivesByTypeStatusAndRefYears } from '../../okr/util'

interface Props extends AppraisalStepBtns {
  meeting: FullMeetingFragment
}

export const AppraisalMeetingStepPayments: FC<Props> = ({ meeting, backBtn, nextBtn }) => {
  const { t } = useTranslation()
  const { currencyConversionData } = useCurrency()
  const isAppraisalUpdateMeeting = meeting.type === MeetingType.AppraisalUpdate
  const { loading, error, data } = useOneOffPaymentTargetPaymentTargetCorrectionPaymentObjectivesAndTargetBonusOfYear(
    meeting.employee.id,
    meeting.id,
    meeting.targetsCalculationHelper.pastTargetsReferenceYear,
    true,
  )

  const {
    loading: countrySettingsLoading,
    error: countrySettingsError,
    data: countrySettingsData,
  } = useCountrySettings(meeting.employee.currentContract!.legalEntity.country)

  if (
    loading ||
    error ||
    !data ||
    !meeting.meetingSteps ||
    countrySettingsLoading ||
    countrySettingsError ||
    !countrySettingsData ||
    !countrySettingsData.getCountrySettings
  ) {
    return (
      <LoadingAndErrors
        isLoading={loading || countrySettingsLoading}
        hasNoData={!data || !countrySettingsData || !countrySettingsData.getCountrySettings}
        error={error || countrySettingsError}
        showSpinner
      />
    )
  }

  const showNotOnFirstOfMonthDisclaimer = countrySettingsData.getCountrySettings.paymentsAndChangesDisclaimerVisible

  const approvedMonetaryTargets = filterObjectivesByTypeStatusAndRefYears(
    data.objectivesInMeeting,
    true,
    [ObjectiveStatus.Approved, ObjectiveStatus.PastApproved],
    [meeting.targetsCalculationHelper.pastTargetsReferenceYear, meeting.targetsCalculationHelper.newTargetsReferenceYear],
  )

  const unknownCurrenciesForConversion: Currency[] = [
    ...new Set(
      approvedMonetaryTargets
        .filter((obj): boolean => {
          if (obj.currency! !== meeting.employee.currentContract!.currencyForPayments) {
            const conversionRate: number | undefined = retrieveCurrencyConversionRate(
              obj.currency!,
              meeting.employee.currentContract!.currencyForPayments as Currency,
              currencyConversionData,
            )
            if (!conversionRate) {
              return true
            }
          }
          return false
        })
        .map((obj) => obj.currency!),
    ),
  ]

  const targetBonusToBePaid: number = !!unknownCurrenciesForConversion.length
    ? 0
    : approvedMonetaryTargets.reduce(
        (sum, obj) =>
          obj.achievedAmount
            ? sum +
              Number(
                (
                  obj.achievedAmount *
                  retrieveCurrencyConversionRate(
                    obj.currency!,
                    meeting.employee.currentContract!.currencyForPayments as Currency,
                    currencyConversionData,
                  )!
                ).toFixed(2),
              )
            : sum,
        0,
      )

  const objectivePayment = data.objectivePayment
    ? data.objectivePayment
    : { id: null, meetingId: meeting.id, status: ObjectivePaymentStatus.Draft, deduction: null, paymentDateValid: true }

  const deduction = objectivePayment.deduction ? objectivePayment.deduction : 0

  const paymentDateExists = !!objectivePayment.paymentDate
  const targetBonusToBePaidNegative = round2digits(targetBonusToBePaid - deduction) < 0
  const isOneOffPaymentValid = data.oneOffPaymentByMeetingId ? data.oneOffPaymentByMeetingId.paymentDateValid : true

  const isOneOffPaymentNotAllowed = data.isOneOffPaymentNotAllowed
  const existingOneOffToBeRemoved = !!data.oneOffPaymentByMeetingId && isOneOffPaymentNotAllowed

  const targetCorrectionPaymentAllowedYears = data.targetCorrectionPaymentAllowedYears

  const existingWrongTCPayments = existingWrongTCPaymentsForLowEmployee(
    meeting.appraisalSeason,
    targetCorrectionPaymentAllowedYears,
    data.targetCorrectionPaymentByMeetingId,
  )

  const isTcpPaymentDateValid = !!data.targetCorrectionPaymentByMeetingId ? data.targetCorrectionPaymentByMeetingId.paymentDateValid : true

  const atLeastOneReferenceYearAvailable =
    targetCorrectionPaymentAllowedYears.currentYearAllowed || targetCorrectionPaymentAllowedYears.previousYearAllowed

  const existingWrongTCPaymentsTooltip = data.targetCorrectionPaymentByMeetingId
    ? atLeastOneReferenceYearAvailable
      ? t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingWrongTCPaymentsForLowEmployeeToBeChanged', {
          year: data.targetCorrectionPaymentByMeetingId.referenceYear,
        })
      : t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingWrongTCPaymentsForLowEmployeeToBeRemoved', {
          year: data.targetCorrectionPaymentByMeetingId.referenceYear,
        })
    : ''

  const atLeastOneObjectiveWithWrongCurrency = approvedMonetaryTargets.some(
    (obj) => isDefined(obj.targetAmount) && obj.currency !== meeting.employee.currentContract!.currencyForPayments,
  )

  const currentStepError =
    !!unknownCurrenciesForConversion.length ||
    targetBonusToBePaidNegative ||
    !isOneOffPaymentValid ||
    !objectivePayment.paymentDateValid ||
    existingOneOffToBeRemoved ||
    existingWrongTCPayments ||
    atLeastOneObjectiveWithWrongCurrency ||
    !isTcpPaymentDateValid

  const currentStepStatus = currentStepError ? MeetingStepStatus.Incomplete : MeetingStepStatus.Done
  const currentStepTooltip = !!unknownCurrenciesForConversion.length
    ? t('appraisalMeeting.stepConclusion.disabledButtonLabel.unknownConversionRate', {
        currentContractCurrency: meeting.employee.currentContract!.currencyForPayments,
        unknownCurrencies: unknownCurrenciesForConversion.join(', '),
      })
    : atLeastOneObjectiveWithWrongCurrency
    ? t('appraisalMeeting.stepConclusion.disabledButtonLabel.atLeastOneMonetaryTargetWithWrongCurrency')
    : targetBonusToBePaidNegative
    ? t('appraisalMeeting.stepConclusion.disabledButtonLabel.targetBonusToBePaidNegative')
    : !paymentDateExists && targetBonusToBePaid
    ? t('appraisalMeeting.stepConclusion.disabledButtonLabel.missingObjectivePaymentDate')
    : !objectivePayment.paymentDateValid
    ? t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidObjectivePaymentDate')
    : existingOneOffToBeRemoved
    ? t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingOneOffPaymentsForLowEmployee')
    : !isOneOffPaymentValid
    ? t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidOneOffPaymentDate')
    : existingWrongTCPayments
    ? existingWrongTCPaymentsTooltip
    : !isTcpPaymentDateValid
    ? t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidTargetCorrectionPaymentDate')
    : ''

  return (
    <div data-e2e-appraisal-step-payments>
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.Payments} stepStatus={currentStepStatus} />
      <MeetingBackground withFooter>
        <PlannedPaymentContainer
          employeeId={meeting.employee.id}
          showNotOnFirstOfMonthDisclaimer={showNotOnFirstOfMonthDisclaimer}
          meeting={meeting}
          earliestPaymentCycle={meeting.nextPaymentCycle}
          objectivePayment={objectivePayment}
          objectives={data.objectivesInMeeting}
          handleLowTag={meeting.requiresSpecialHandlingOfLowTag}
        />
        <EditableOneOffPayment
          meetingId={meeting.id}
          showNotOnFirstOfMonthDisclaimer={showNotOnFirstOfMonthDisclaimer}
          nextPaymentcycle={meeting.nextPaymentCycle}
          oneOffPayment={data.oneOffPaymentByMeetingId ? data.oneOffPaymentByMeetingId : undefined}
          hasDivider={isAppraisalUpdateMeeting}
          isOneOffPaymentNotAllowed={isOneOffPaymentNotAllowed}
        />
        {isAppraisalUpdateMeeting && (
          <EditableTargetCorrectionPayment
            meetingId={meeting.id}
            appraisalSeasonYear={meeting.appraisalSeason.year}
            showNotOnFirstOfMonthDisclaimer={showNotOnFirstOfMonthDisclaimer}
            nextPaymentcycle={meeting.nextPaymentCycle}
            targetCorrectionPayment={data.targetCorrectionPaymentByMeetingId ? data.targetCorrectionPaymentByMeetingId : undefined}
            targetCorrectionPaymentAllowedYears={targetCorrectionPaymentAllowedYears}
            existingWrongTCPayments={existingWrongTCPayments}
          />
        )}
      </MeetingBackground>

      <AppraisalFooter>
        <PaymentFooter
          objectives={data.objectivesInMeeting}
          oneOffPayment={
            data.oneOffPaymentByMeetingId && !!data.oneOffPaymentByMeetingId.resultAmount
              ? data.oneOffPaymentByMeetingId.resultAmount
              : undefined
          }
          objectivePayment={data.objectivePayment ? data.objectivePayment : undefined}
          targetCorrectionPayment={
            isAppraisalUpdateMeeting && data.targetCorrectionPaymentByMeetingId ? data.targetCorrectionPaymentByMeetingId : undefined
          }
        />
      </AppraisalFooter>
      <PerFlexRow justify="space-between">
        {backBtn && backBtn(currentStepStatus)}
        {nextBtn && nextBtn(currentStepStatus, false, currentStepTooltip)}
      </PerFlexRow>
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.Payments} stepStatus={currentStepStatus} />
    </div>
  )
}
